import { ComponentNames } from "../../constant/component-constant";
import { arraytoObj, cleanEmpty } from "../../helper/sb-fixer";
import { logAdapterEvent, logError } from "../../helper/logger";

interface IDtvMarqueeAdapterArgs {
  content: any;
  svodGenreData?: any;
  embeddedSvods?: any;
  epochOffers?: any;
  cmsPageName?: string;
  packagesConfig?: any;
}

const ADAPTER_NAME_NAME = "DtvMarqueeAdapter";
const PRICE_REGEX = /^\$(\d+)(\.\d+)?$/;

// Generated by Copilot- Start - 2026-04-12 17:56:59
/**
 * @description
 * Inject dynamic content into marquee fields using Epoch pricing data.
 * Replaces CMS placeholders with live values from the Epoch API, following
 * the same conventions used in `package-carousel-adapter.ts`.
 *
 * **Product type resolution** (via `packagesConfig`):
 * | Config Type    | Product Type | Epoch Table                         | ID Transform              | Price Shape                          |
 * |----------------|--------------|-------------------------------------|---------------------------|--------------------------------------|
 * | `genre`        | GENRE        | `genreSvodOffers.offerPrices`       | as-is (e.g. ATT_TV_MYKIDS)| `{ price, regularPrice }` object     |
 * | `genre-mini`   | GENRE        | `genreSvodOffers.offerPrices`       | as-is                     | `{ price, regularPrice }` object     |
 * | `internet`     | DTVI         | `dtviOffersPrices.offerPrices`      | strip `ATT_TV_`, join " " | raw number                           |
 * | `satellite`    | DTVS         | `dtvsOffersPrices.offerPrices`      | strip `DTV_TV_`/`DTV_`    | `localPrice[epochId]` number         |
 *
 * **Supported placeholders** (can appear in any field):
 * | Placeholder        | Resolved From                             |
 * |--------------------|-------------------------------------------|
 * | `[offer_price]`    | Epoch offer/promo price (formatted $X.XX) |
 * | `[regular_price]`  | Epoch regular price (formatted $X.XX)     |
 * | `[promo_duration]` | Epoch promo benefit duration (months)  |
 *
 * **Fields scanned:** pricePoint, strikeText, offerRibbon, pricePointIntro, description, legalText
 *
 * **Conditional rendering:**
 * - The component already gates rendering on `?.content` presence — the adapter
 *   does NOT hide/show most fields.
 * - Exception: `strikeText` and `offerRibbon` are cleared when no promo is active
 *   (regularPrice is absent or equals offerPrice) to prevent showing stale promo content.
 *
 * **Price splitting:**
 * - `pricePoint.content` receives the dollar portion (e.g., `$29`)
 * - `pricePoint.spanText.content` receives the fractional + suffix (e.g., `.99/mo.`)
 *
 * @param content - The marquee content object (already cleaned/fixed) — mutated in place
 * @param epochOffers - Full Epoch offers data
 * @param packagesConfig - Package configuration for offer lookups
 */

/**
 * Derive the Epoch lookup key from a packageConfig ID based on product type.
 * Mirrors `getEpochId()` in `package-carousel-adapter.ts`.
 */
const getEpochIdForMarquee = (productType: string, cardId: string): string => {
  switch (productType) {
    case "DTVI":
      return cardId
        ?.replace(/^ATT_TV_/, "")
        ?.split("_")
        ?.join(" ");
    case "DTVS":
      return cardId
        ?.replace(/^DTV_TV_/, "")
        ?.replace(/^DTV_/, "")
        ?.split("_")
        ?.join(" ");
    default:
      // GENRE / GENRE-MINI — use as-is
      return cardId;
  }
};

/**
 * Map `packagesConfig.type` to a canonical product type string.
 */
const resolveProductType = (configType: string): string | null => {
  switch (configType?.toLowerCase()) {
    case "genre":
    case "genre-mini":
      return "GENRE";
    case "internet":
      return "DTVI";
    case "satellite":
      return "DTVS";
    default:
      return null;
  }
};

/**
 * Look up the offer price from the correct Epoch pricing table.
 * Returns `{ offerPrice, regularPrice, promoDuration }` (numbers or null).
 */
const resolveEpochPrice = (
  productType: string,
  epochId: string,
  packName: string,
  epochOffers: any
): { offerPrice: number | null; regularPrice: number | null; promoDuration?: number | null } => {
  const fallback = {
    offerPrice: null,
    regularPrice: null
  };

  switch (productType) {
    case "GENRE": {
      const offerPrices = epochOffers?.genreSvodOffers?.offerPrices;
      if (!Array.isArray(offerPrices)) {
        return fallback;
      }

      const entry = offerPrices.find(
        (priceList: any) => packName in (priceList || {})
      );
      if (!entry?.[packName]) {
        return fallback;
      }

      const priceObj = entry[packName];
      return {
        offerPrice: priceObj?.price ?? priceObj?.regularPrice ?? null,
        regularPrice: priceObj?.regularPrice ?? null,
        promoDuration: priceObj?.promoDuration ?? null
      };
    }
    case "DTVI": {
      const offerPrices = epochOffers?.dtviOffersPrices?.offerPrices;
      if (!Array.isArray(offerPrices)) {
        return fallback;
      }

      const entry = offerPrices.find(
        (priceList: any) => epochId in (priceList || {})
      );
      const val = entry?.[epochId];
      if (typeof val !== "number") {
        return fallback;
      }
      return {
        offerPrice: val,
        regularPrice: null
      };
    }
    case "DTVS": {
      const offerPrices = epochOffers?.dtvsOffersPrices?.offerPrices;
      if (!Array.isArray(offerPrices)) {
        return fallback;
      }

      const entry = offerPrices.find(
        (priceList: any) => priceList?.localPrice?.[epochId] != null
      );
      const val = entry?.localPrice?.[epochId];
      if (typeof val !== "number") {
        return fallback;
      }
      return {
        offerPrice: val,
        regularPrice: null
      };
    }
    default:
      return fallback;
  }
};

const injectDynamicContent = (
  content: any,
  epochOffers: any,
  packagesConfig: any
): void => {
  try {
    const packName = content?.config?.packName;
    if (!packName || !epochOffers) {
      return;
    }

    // Determine product type from packagesConfig
    const pkgConfig = Array.isArray(packagesConfig) ?
      packagesConfig.find((pkg: any) => pkg?.id === packName) :
      null;
    const productType = pkgConfig ?
      resolveProductType(pkgConfig.type) :
      null;

    // If packName isn't in packagesConfig, fall back to GENRE for backward compat.
    // If packagesConfig IS present but type is unrecognized, bail out — wrong table.
    const effectiveType = productType ?? (pkgConfig ? null : "GENRE");
    if (!effectiveType) {
      logError(ADAPTER_NAME_NAME, new Error("Unrecognized packagesConfig type"), {
        step: "injectDynamicContent",
        packName,
        configType: pkgConfig?.type
      });
      return;
    }
    const epochId = getEpochIdForMarquee(effectiveType, packName);

    // Resolve price from the correct Epoch table
    const { offerPrice, regularPrice, promoDuration } = resolveEpochPrice(
      effectiveType,
      epochId,
      packName,
      epochOffers
    );

    // If Epoch has no price, try fallback from packagesConfig
    const fallbackPrice = pkgConfig?.price != null ? Number(pkgConfig.price) : null;
    const effectiveOfferPrice = offerPrice ?? fallbackPrice;

    if (effectiveOfferPrice == null) {
      return;
    }

    // Format prices
    const formattedOfferPrice =
      typeof effectiveOfferPrice === "number" ? `$${effectiveOfferPrice.toFixed(2)}` : null;
    const formattedRegularPrice =
      typeof regularPrice === "number" ? `$${regularPrice.toFixed(2)}` : null;

    // Promo is active when regularPrice exists and differs from offer price
    const promoActive =
      typeof effectiveOfferPrice === "number" &&
      typeof regularPrice === "number" &&
      effectiveOfferPrice !== regularPrice;

    // Extract promo duration — sourced from all product types now
    //   GENRE: promoDuration from genre resolver
    //   DTVI/DTVS: benefits[0].periodMo from their resolver
    const effectivePromoDuration = promoDuration ?? null;

    // Generic placeholder replacement — any field can contain any placeholder
    const replacePlaceholders = (text: string): string => {
      let result = text;
      if (formattedOfferPrice) {
        result = result.replace(/\[offer_price\]/g, formattedOfferPrice);
      }
      if (formattedRegularPrice) {
        result = result.replace(/\[regular_price\]/g, formattedRegularPrice);
      }
      if (effectivePromoDuration) {
        result = result.replace(/\[promo_duration\]/g, String(effectivePromoDuration));
      } else if (result.includes("[promo_duration]")) {
        logError(ADAPTER_NAME_NAME, new Error("[promo_duration] placeholder found but no duration available"), {
          step: "replacePlaceholders",
          packName,
          productType: effectiveType
        });
      }
      return result;
    };

    // --- pricePoint: replace placeholders and split into mainPrice + fractional ---
    if (content?.pricePoint?.content) {
      const resolvedPrice = replacePlaceholders(content.pricePoint.content);

      const dollarMatch = resolvedPrice.match(PRICE_REGEX);
      if (dollarMatch) {
        const mainPrice = `$${dollarMatch[1]}`;
        const fractionalPart = dollarMatch[2] ?? "";

        // Extract non-numeric suffix from CMS spanText (e.g., "/mo.")
        let cmsSuffix = "";
        if (content.pricePoint.spanText?.content) {
          cmsSuffix = content.pricePoint.spanText.content.replace(/^[\d.\s]+/, "");
        }

        content.pricePoint = {
          ...content.pricePoint,
          content: mainPrice,
          spanText: fractionalPart ?
            {
              ...content.pricePoint.spanText,
              content: `${fractionalPart}${cmsSuffix}`
            } :
            cmsSuffix ?
              {
                ...content.pricePoint.spanText,
                content: cmsSuffix
              } :
              undefined
        };
      } else {
        content.pricePoint = {
          ...content.pricePoint,
          content: resolvedPrice
        };
      }
    }

    // --- strikeText: replace placeholders, but hide when no promo ---
    if (content?.strikeText?.content && typeof content.strikeText.content === "string") {
      if (promoActive) {
        content.strikeText = {
          ...content.strikeText,
          content: replacePlaceholders(content.strikeText.content)
        };
      } else {
        content.strikeText = {
          ...content.strikeText,
          content: ""
        };
      }
    }

    // --- offerRibbon: replace placeholders, but hide when no promo ---
    if (content?.offerRibbon?.content && typeof content.offerRibbon.content === "string") {
      if (promoActive) {
        content.offerRibbon = {
          ...content.offerRibbon,
          content: replacePlaceholders(content.offerRibbon.content)
        };
      } else {
        content.offerRibbon = {
          ...content.offerRibbon,
          content: ""
        };
      }
    }

    // --- pricePointIntro: replace placeholders ---
    if (content?.pricePointIntro?.content && typeof content.pricePointIntro.content === "string") {
      content.pricePointIntro = {
        ...content.pricePointIntro,
        content: replacePlaceholders(content.pricePointIntro.content)
      };
    }

    // --- description: replace placeholders in all description items ---
    if (content?.description && Array.isArray(content.description)) {
      content.description = content.description.map((item: any) => {
        if (item?.desc?.content && typeof item.desc.content === "string") {
          const replaced = replacePlaceholders(item.desc.content);
          if (replaced !== item.desc.content) {
            return {
              ...item,
              desc: {
                ...item.desc,
                content: replaced
              }
            };
          }
        }
        return item;
      });
    }

    // --- legalText: replace placeholders ---
    if (content?.legalText?.content && typeof content.legalText.content === "string") {
      content.legalText = {
        ...content.legalText,
        content: replacePlaceholders(content.legalText.content)
      };
    }
  } catch (error) {
    logError(ADAPTER_NAME_NAME, error, {
      step: "Error injecting dynamic content into marquee: injectDynamicContent"
    });
    // Graceful fallback: content remains unchanged
  }
};
// Generated by Copilot- End - 2026-04-12 17:56:59

export const dtvMarqueeAdapter = ({
  content: componentProps,
  svodGenreData,
  embeddedSvods,
  epochOffers,
  cmsPageName,
  packagesConfig
}: IDtvMarqueeAdapterArgs): any => {
  try {
    logAdapterEvent(ADAPTER_NAME_NAME, "Adapting data for DTV Marquee", {
      cmsPageName
    });
    const content = cleanEmpty(arraytoObj(componentProps));

    // --- Dynamic content injection for all marquee variants (non-SVOD and SVOD) ---
    // Replaces [offer_price], [regular_price], [promo_duration] placeholders
    // and conditionally shows/hides strikeText and offerRibbon based on promo state.
    // Falls back to static CMS values on error.
    injectDynamicContent(content, epochOffers, packagesConfig);

    if (svodGenreData && content?.config?.enableSVOD) {
      const genrePackName = content.config?.packName;
      const svodMapping = epochOffers?.genreSvodOffers?.svodMapping || [];
      const svodSKUs = svodMapping?.[genrePackName] || [];
      const filteredSvods = embeddedSvods?.filter(svod => svodSKUs.includes(svod?.offerCode) && !svod?.dtvMarqueeData?.disable);
      content.filteredSvods = filteredSvods;
      // Calculate and append SVOD price for the genre pack
      const svodPrice = epochOffers?.genreSvodOffers?.offerPrices?.find(
        priceList => genrePackName in priceList
      );
      if (svodPrice) {
        const priceObj = svodPrice[genrePackName];
        const numericPrice =
          priceObj?.price ?? priceObj?.regularPrice;
        if (typeof numericPrice === 'number') {
          const svodPackagePrice = `$${numericPrice.toFixed(2)}`;
          content.svodPackagePrice = svodPackagePrice;
          const dollarMatch = svodPackagePrice.match(PRICE_REGEX);
          if (dollarMatch) {
            const mainPrice = `$${dollarMatch[1]}`;
            const fractionalPart = dollarMatch[2] ?? '';
            content.svodParsedPrice = {
              mainPrice,
              fractionalPart
            };
          }else{
            content.svodParsedPrice = {
              mainPrice: svodPackagePrice,
              fractionalPart: null
            };
          }
        }
      }

      // Generate combined SVOD text using name field and content.svodOfferText
      const svodNames = filteredSvods
        .map(svod => (svod?.displayName || svod?.name)?.trim())
        .filter(Boolean);
      let combinedSvodText = "";
      if (
        svodNames?.length > 0 &&
        content.svodOfferText &&
        (content?.svodOfferText?.label || content?.svodOfferText?.symbol)
      ) {
        const label = content?.svodOfferText?.label || "";
        const symbol = content?.svodOfferText?.symbol || "";
        if (svodNames?.length === 1) {
          combinedSvodText = `${label} ${svodNames[0]}${symbol}`.trim();
        } else {
          const namesExceptLast = svodNames.slice(0, -1);
          const last = svodNames[svodNames.length - 1];
          combinedSvodText = `${label} ${namesExceptLast.join(", ")}${
            namesExceptLast.length ? " & " : ""
          }${last}${symbol}`.trim();
        }
        if (combinedSvodText) {
          content.combinedSvodText = combinedSvodText;
        }
      }

      // Generated by Copilot- Start - 2026-04-12 17:56:59
      // Pre-merge svodParsedPrice into pricePoint so the component renders
      // directly from props without reconstructing the price at render time.
      if (content.svodParsedPrice && content.pricePoint) {
        let cmsSuffix = "";
        if (content.pricePoint?.spanText?.content) {
          cmsSuffix = content.pricePoint.spanText.content.replace(/^[\d.\s]+/, "");
        }
        content.pricePoint = {
          ...content.pricePoint,
          content: content.svodParsedPrice.mainPrice,
          spanText: content.svodParsedPrice.fractionalPart ?
            {
              ...content.pricePoint.spanText,
              content: `${content.svodParsedPrice.fractionalPart}${cmsSuffix}`
            } :
            undefined
        };
      }
      // Generated by Copilot- End - 2026-04-12 17:56:59
    }
    return {
      props: {
        ...content,
        ...(content.options && { gnavOptions: content.options }),
        options: {
          id: content.id || null,
          ...(content.options && { ...content.options })
        }
      },
      type: ComponentNames.DTVMARQUEE
    };
  } catch (error) {
    logError(ADAPTER_NAME_NAME, error, {});
  }
};

export default dtvMarqueeAdapter;
